import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import HistoryIcon from '@mui/icons-material/History'
import { Button } from '@mui/material'
import { useListThreads } from '@api/agent-runs/agent-runs'
import { useSnackbar } from '@ui/snackbar'

interface Props {
  projectId: string
  threadId: string
  onSelectThread: (threadId: string, model: string | null | undefined) => void
}

/**
 * "Letzten Chat öffnen", in the middle of a new, empty chat: back to the
 * project's most recent earlier chat in one click. Shown only for a chat the
 * API does not know yet; a stored thread whose history is still loading is
 * empty for a moment too, and must not offer it.
 */
const OpenLastThreadButton = ({
  projectId,
  threadId,
  onSelectThread,
}: Props): ReactElement | null => {
  const { enqueueSnackbar } = useSnackbar()
  const { data } = useListThreads(
    { project_guid: projectId },
    {
      query: {
        // Newest first; fresh for every new chat, so it is the latest one.
        staleTime: 0,
        onError: (error: AxiosError) => {
          enqueueSnackbar(buildErrorMessage(error, 'Chats konnten nicht geladen werden'), {
            variant: 'error',
          })
        },
      },
    },
  )

  const threads = data?.threads ?? []
  if (threads.some(thread => thread.id === threadId)) return null
  const last = threads[0]
  if (!last) return null

  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<HistoryIcon fontSize="small" />}
      onClick={() => onSelectThread(last.id, last.model)}
      data-cy="ai-chat-open-last-thread"
    >
      Letzten Chat öffnen
    </Button>
  )
}

export default OpenLastThreadButton
